import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate, fmtMoneyCur, CREDITO_ESTATUS } from "../lib/format";
import { Card } from "../components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Plus, Landmark } from "lucide-react";

export const CREDITO_TIPOS = ["Crédito puente", "Crédito de construcción", "Crédito simple", "Revolvente", "Mezzanine", "Aportación de socio"];
export const EST_BADGE = {
  "En negociación": "bg-slate-100 text-slate-600", "Term sheet firmado": "bg-sky-50 text-sky-700",
  "En condiciones precedentes": "bg-amber-50 text-amber-700", "Vigente/Disponiendo": "bg-cyan-50 text-cyan-700",
  "En amortización": "bg-emerald-50 text-emerald-700", "Liquidado": "bg-slate-50 text-slate-400",
  "En incumplimiento": "bg-red-100 text-red-800",
};

export default function Creditos() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canFin = user && (user.role === "admin" || user.role === "financiero");
  const [creditos, setCreditos] = useState(null);
  const [nuevo, setNuevo] = useState(null);
  const load = () => api.get(`/properties/${pid}/creditos`).then((r) => setCreditos(r.data));
  useEffect(() => { load(); }, [pid]); // eslint-disable-line

  const crear = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/creditos", { property_id: pid, acreditante_nombre: nuevo.acreditante || "",
        tipo: nuevo.tipo || "Crédito puente", moneda: nuevo.moneda || "MXN", monto_autorizado: Number(nuevo.monto) || 0,
        plazo_meses: Number(nuevo.plazo) || 0 });
      toast.success("Crédito creado"); setNuevo(null); navigate(`/p/${pid}/creditos/${data.id}`);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-5">
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-[#00A3B5] font-bold mb-1">Módulo Financiamiento</p>
          <h1 className="font-display text-3xl font-bold text-[#002B49]">Préstamos bancarios y créditos</h1>
        </div>
        {canFin && <Button data-testid="nuevo-credito-button" onClick={() => setNuevo({})} className="h-9 gap-2 bg-[#00A3B5] hover:bg-[#008C9C] text-white"><Plus size={14} /> Nuevo crédito</Button>}
      </div>
      {creditos?.some((c) => c.estatus === "En incumplimiento") && (
        <p className="text-sm font-bold text-white bg-red-600 rounded-md px-4 py-2.5 mb-4" data-testid="banner-incumplimiento">⚠ HAY CRÉDITOS EN INCUMPLIMIENTO — revisa covenants y pagos de inmediato</p>)}
      {!creditos ? <p className="text-slate-400 animate-pulse">Cargando créditos...</p> : creditos.length === 0 ? (
        <Card className="p-10 text-center">
          <Landmark size={36} className="mx-auto text-slate-200 mb-3" />
          <p className="text-slate-500 font-semibold">Sin créditos registrados</p>
          <p className="text-sm text-slate-400 mt-1">Da de alta los créditos que financian el proyecto para controlar CPs, disposiciones, servicio de deuda y covenants.</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {creditos.map((c) => {
            const pct = c.monto_autorizado ? Math.min(100, Math.round(100 * c.monto_dispuesto / c.monto_autorizado)) : 0;
            return (
              <Card key={c.id} className="p-5 cursor-pointer hover:border-[#00A3B5] transition-colors" testId={`credito-card-${c.id}`}>
                <div onClick={() => navigate(`/p/${pid}/creditos/${c.id}`)}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-display font-bold text-[#002B49]">{c.acreditante_nombre || "Sin acreditante"} · {c.tipo}</p>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${EST_BADGE[c.estatus]}`}>{c.estatus}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Tasa {c.tasa_anual?.toFixed(2)}% · {c.plazo_meses} meses · {c.moneda}</p>
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span>Dispuesto {fmtMoneyCur(c.monto_dispuesto, c.moneda)}</span>
                      <span>Autorizado {fmtMoneyCur(c.monto_autorizado, c.moneda)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#00A3B5]" style={{ width: `${pct}%` }} /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                    {[["Saldo insoluto", fmtMoneyCur(c.saldo_insoluto, c.moneda)], ["CPs", `${c.cps_aceptadas}/${c.cps_total}`],
                      ["Próx. pago", c.proximo_pago ? fmtDate(c.proximo_pago.fecha) : "—"]].map(([l, v]) => (
                      <div key={l} className="bg-slate-50 rounded-md p-2">
                        <p className="font-display font-bold text-sm text-[#002B49]">{v}</p>
                        <p className="text-[9px] uppercase tracking-wider text-slate-500">{l}</p>
                      </div>))}
                  </div>
                </div>
              </Card>);
          })}
        </div>
      )}
      <Dialog open={!!nuevo} onOpenChange={(v) => !v && setNuevo(null)}>
        <DialogContent className="max-w-md" data-testid="credito-modal">
          <DialogHeader><DialogTitle className="font-display text-[#002B49]">Nuevo crédito</DialogTitle></DialogHeader>
          {nuevo && <form onSubmit={crear} className="space-y-3">
            <div className="space-y-1.5"><Label>Acreditante (banco/fondo) *</Label>
              <Input data-testid="credito-acreditante-input" required value={nuevo.acreditante || ""} onChange={(e) => setNuevo({ ...nuevo, acreditante: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Tipo</Label>
                <Select value={nuevo.tipo || "Crédito puente"} onValueChange={(v) => setNuevo({ ...nuevo, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CREDITO_TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Moneda</Label>
                <Select value={nuevo.moneda || "MXN"} onValueChange={(v) => setNuevo({ ...nuevo, moneda: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Monto autorizado</Label>
                <Input data-testid="credito-monto-input" type="number" value={nuevo.monto || ""} onChange={(e) => setNuevo({ ...nuevo, monto: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Plazo (meses)</Label>
                <Input type="number" value={nuevo.plazo || ""} onChange={(e) => setNuevo({ ...nuevo, plazo: e.target.value })} /></div>
            </div>
            <p className="text-xs text-slate-400">Los demás términos (tasa, garantías, CPs, covenants) se capturan dentro del expediente.</p>
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setNuevo(null)}>Cancelar</Button>
              <Button data-testid="credito-crear-button" type="submit" className="bg-[#00A3B5] hover:bg-[#008C9C] text-white">Crear expediente</Button></div>
          </form>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
